import { COLOR_SLOTS, ThemeColors, TApplicationUIConfigForm } from "./uiConfig.types";

// The form IS the configuration payload; the BE stores it as a JSON object, so no serialization on write.
export const toConfiguration = (form: TApplicationUIConfigForm): Record<string, unknown> => ({ ...form });

// Keep only the slots the app still knows about, defaulting each from the theme-seeded value. Configs
// written before a slot was retired (NG-8572 dropped brandingPanelBg / brandingPanelText) still carry it;
// picking by COLOR_SLOTS instead of spreading means those stale keys never reach the form — so a later
// Save doesn't write them back — while a config missing a slot still hydrates from the default.
const pickKnownSlots = (defaults: ThemeColors, stored?: Partial<ThemeColors>): ThemeColors =>
    COLOR_SLOTS.reduce((acc, slot) => ({ ...acc, [slot]: stored?.[slot] ?? defaults[slot] }), {} as ThemeColors);

// Merge the stored config over the given defaults (theme-seeded) so a config written by an
// older/newer schema — or missing color slots — still hydrates the form safely.
// The BE's JSON scalar can come back either as a parsed object or as a JSON string, so handle both.
export const fromConfiguration = (configuration: unknown, defaults: TApplicationUIConfigForm): TApplicationUIConfigForm => {
    let parsed: Partial<TApplicationUIConfigForm> = {};
    if (typeof configuration === "string") {
        try {
            parsed = JSON.parse(configuration) as Partial<TApplicationUIConfigForm>;
        } catch {
            parsed = {};
        }
    } else if (configuration && typeof configuration === "object") {
        parsed = configuration;
    }

    return {
        ...defaults,
        ...parsed,
        content: { ...defaults.content, ...parsed.content },
        colors: {
            light: pickKnownSlots(defaults.colors.light, parsed.colors?.light),
            dark: pickKnownSlots(defaults.colors.dark, parsed.colors?.dark),
        },
    };
};
